import { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { usePageTitle } from '@/hooks/usePageTitle';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Card, CardHeader, CardTitle, CardDescription, CardContent } from '@/components/ui/card';
import { Eye, EyeOff, Lock, Loader2, CheckCircle, XCircle } from 'lucide-react';
import { supabase } from '@/lib/supabase';
import { updatePassword } from '@/lib/auth';
import { PageTransition } from '@/components/PageTransition';

const ResetPasswordPage = () => {
  const navigate = useNavigate();
  usePageTitle('Reset Password');
  const [newPassword, setNewPassword] = useState('');
  const [confirmPassword, setConfirmPassword] = useState('');
  const [showNewPassword, setShowNewPassword] = useState(false);
  const [showConfirmPassword, setShowConfirmPassword] = useState(false);
  const [isLoading, setIsLoading] = useState(false);
  const [isCheckingSession, setIsCheckingSession] = useState(true);
  const [error, setError] = useState('');
  const [hasValidSession, setHasValidSession] = useState(false);
  const [isSuccess, setIsSuccess] = useState(false);
  const [userEmail, setUserEmail] = useState('');

  useEffect(() => {
    // Check if user has a valid session from the recovery link
    const checkSession = async () => {
      try {
        console.log('Checking for password recovery session...');

        // Get the current session
        const { data: { session }, error: sessionError } = await supabase.auth.getSession();

        if (sessionError) {
          console.error('Session error:', sessionError);
          setHasValidSession(false);
          setError('Invalid or expired reset link. Please request a new password reset.');
        } else if (session?.user) {
          console.log('Valid recovery session found for:', session.user.email);
          setHasValidSession(true);
          setUserEmail(session.user.email);
        } else {
          console.log('No valid session found');
          setHasValidSession(false);
          setError('Invalid or expired reset link. Please request a new password reset.');
        }
      } catch (err) {
        console.error('Error checking session:', err);
        setHasValidSession(false);
        setError('An error occurred. Please request a new password reset.');
      } finally {
        setIsCheckingSession(false);
      }
    };

    checkSession();
  }, []);

  const handleResetPassword = async (e) => {
    e.preventDefault();
    setError('');

    if (newPassword !== confirmPassword) {
      setError('Passwords do not match');
      return;
    }

    if (newPassword.length < 8) {
      setError('Password must be at least 8 characters');
      return;
    }

    setIsLoading(true);

    try {
      console.log('Updating password...');

      // Update password using Supabase's updateUser (user is already authenticated via recovery link)
      const { error: updateError } = await updatePassword(newPassword);

      if (updateError) {
        console.error('Password update error:', updateError);
        setError(updateError.message || 'Failed to update password. Please try again.');
      } else {
        console.log('Password updated successfully');
        setIsSuccess(true);

        // Redirect to login after 2 seconds
        setTimeout(() => {
          navigate('/login', {
            state: { message: 'Password updated successfully. Please login with your new password.' }
          });
        }, 2000);
      }
    } catch (err) {
      console.error('Unexpected error:', err);
      setError('An unexpected error occurred. Please try again.');
    } finally {
      setIsLoading(false);
    }
  };

  // Show loading while checking session
  if (isCheckingSession) {
    return (
      <PageTransition>
        <div className="min-h-screen flex items-center justify-center p-8 bg-gradient-to-br from-slate-50 via-white to-blue-50">
          <div className="w-full max-w-md">
            <Card className="border-0 shadow-xl">
              <CardContent className="pt-8 pb-8">
                <div className="text-center">
                  <Loader2 className="h-8 w-8 animate-spin mx-auto mb-4 text-blue-600" />
                  <p className="text-slate-600">Verifying reset link...</p>
                </div>
              </CardContent>
            </Card>
          </div>
        </div>
      </PageTransition>
    );
  }

  // Show success message
  if (isSuccess) {
    return (
      <PageTransition>
        <div className="min-h-screen flex items-center justify-center p-8 bg-gradient-to-br from-slate-50 via-white to-green-50">
          <div className="w-full max-w-md">
            <Card className="border-0 shadow-xl">
              <CardHeader className="text-center pb-8 pt-8">
                <div className="w-20 h-20 bg-green-100 rounded-full flex items-center justify-center mx-auto mb-6">
                  <CheckCircle className="h-10 w-10 text-green-600" />
                </div>
                <CardTitle className="text-2xl font-bold text-slate-900">
                  Password Updated!
                </CardTitle>
                <CardDescription className="text-slate-600 mt-3 text-base">
                  Your password has been successfully updated. Redirecting to login...
                </CardDescription>
              </CardHeader>
            </Card>
          </div>
        </div>
      </PageTransition>
    );
  }

  // Show error if session is invalid
  if (!hasValidSession) {
    return (
      <PageTransition>
        <div className="min-h-screen flex items-center justify-center p-8 bg-gradient-to-br from-slate-50 via-white to-red-50">
          <div className="w-full max-w-md">
            <Card className="border-0 shadow-xl">
              <CardHeader className="text-center pb-8 pt-8">
                <div className="w-20 h-20 bg-red-100 rounded-full flex items-center justify-center mx-auto mb-6">
                  <XCircle className="h-10 w-10 text-red-600" />
                </div>
                <CardTitle className="text-2xl font-bold text-slate-900">
                  Invalid Reset Link
                </CardTitle>
                <CardDescription className="text-slate-600 mt-3 text-base">
                  {error || 'This password reset link is invalid or has expired.'}
                </CardDescription>
              </CardHeader>
              <CardContent className="pb-8">
                <Button
                  onClick={() => navigate('/forgot-password')}
                  className="w-full h-11 bg-slate-900 hover:bg-slate-800"
                >
                  Request New Reset Link
                </Button>
                <div className="text-center mt-4">
                  <Button
                    variant="ghost"
                    onClick={() => navigate('/login')}
                    className="text-sm text-slate-600 hover:text-slate-900"
                  >
                    Back to login
                  </Button>
                </div>
              </CardContent>
            </Card>
          </div>
        </div>
      </PageTransition>
    );
  }

  // Show reset form
  return (
    <PageTransition>
      <div className="min-h-screen flex items-center justify-center p-8 bg-gradient-to-br from-slate-50 via-white to-blue-50">
        <div className="w-full max-w-md">
          <Card className="border-0 shadow-xl">
            <CardHeader className="text-center pb-8 pt-8">
              <div className="w-16 h-16 bg-blue-100 rounded-full flex items-center justify-center mx-auto mb-6">
                <Lock className="w-8 h-8 text-blue-600" />
              </div>
              <CardTitle className="text-2xl font-bold text-slate-900">
                Set New Password
              </CardTitle>
              <CardDescription className="text-slate-600 mt-2 px-4">
                Reset password for: <strong>{userEmail}</strong>
              </CardDescription>
            </CardHeader>

            <CardContent className="pb-8">
              <form onSubmit={handleResetPassword} className="space-y-6">
                {error && (
                  <div className="bg-red-50 border border-red-200 text-red-700 px-4 py-3 rounded-md text-sm">
                    {error}
                  </div>
                )}

                <div className="space-y-2">
                  <Label htmlFor="newPassword" className="text-sm font-medium text-slate-700">
                    New password
                  </Label>
                  <div className="relative">
                    <Input
                      id="newPassword"
                      type={showNewPassword ? 'text' : 'password'}
                      placeholder="Enter new password"
                      value={newPassword}
                      onChange={(e) => setNewPassword(e.target.value)}
                      className="h-11 pr-10"
                      required
                      minLength={8}
                      disabled={isLoading}
                    />
                    <Button
                      type="button"
                      variant="ghost"
                      size="sm"
                      className="absolute right-0 top-0 h-11 px-3 hover:bg-transparent"
                      onClick={() => setShowNewPassword(!showNewPassword)}
                    >
                      {showNewPassword ? (
                        <EyeOff className="h-4 w-4 text-slate-500" />
                      ) : (
                        <Eye className="h-4 w-4 text-slate-500" />
                      )}
                    </Button>
                  </div>
                  <p className="text-xs text-slate-500">Must be at least 8 characters</p>
                </div>

                <div className="space-y-2">
                  <Label htmlFor="confirmPassword" className="text-sm font-medium text-slate-700">
                    Confirm password
                  </Label>
                  <div className="relative">
                    <Input
                      id="confirmPassword"
                      type={showConfirmPassword ? 'text' : 'password'}
                      placeholder="Confirm new password"
                      value={confirmPassword}
                      onChange={(e) => setConfirmPassword(e.target.value)}
                      className="h-11 pr-10"
                      required
                      disabled={isLoading}
                    />
                    <Button
                      type="button"
                      variant="ghost"
                      size="sm"
                      className="absolute right-0 top-0 h-11 px-3 hover:bg-transparent"
                      onClick={() => setShowConfirmPassword(!showConfirmPassword)}
                    >
                      {showConfirmPassword ? (
                        <EyeOff className="h-4 w-4 text-slate-500" />
                      ) : (
                        <Eye className="h-4 w-4 text-slate-500" />
                      )}
                    </Button>
                  </div>
                </div>

                <Button
                  type="submit"
                  disabled={isLoading || !newPassword || !confirmPassword}
                  className="w-full h-11 bg-slate-900 hover:bg-slate-800"
                >
                  {isLoading ? (
                    <>
                      <Loader2 className="w-4 h-4 mr-2 animate-spin" />
                      Updating...
                    </>
                  ) : (
                    'Update password'
                  )}
                </Button>
              </form>

              <div className="text-center mt-6">
                <p className="text-sm text-slate-600">
                  Remember your password?{' '}
                  <Button
                    variant="link"
                    className="px-0 text-slate-900 hover:text-slate-700"
                    onClick={() => navigate('/login')}
                  >
                    Back to login
                  </Button>
                </p>
              </div>
            </CardContent>
          </Card>
        </div>
      </div>
    </PageTransition>
  );
};

export default ResetPasswordPage;
